import { ArrowRight, ExternalLink } from 'lucide-react';

import '@fontsource/inter';

function AwsLogo() {
  return <img src="/aws-marketplace.png" alt="AWS Logo" className="h-4 w-auto" />;
}

function LikaLogo() {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xl font-extrabold tracking-[-0.03em] text-white">LIKA</span>
    </div>
  );
}

function TopBanner() {
  return (
    <div className="flex flex-wrap items-center justify-center gap-4 bg-[#1a2030] px-4 py-2 text-center">
      <span className="text-[13px] text-white font-['Inter']">
        Trust is Proven, Not Assumed. Deploy a private, secure IDP engine directly into your VPC in
        under an hour.
      </span>
      <a
        href="#"
        className="inline-flex items-center gap-1.5 text-[13px] font-medium text-white font-['Inter']"
      >
        <AwsLogo />
        <ExternalLink size={12} />
      </a>
    </div>
  );
}

function Navbar() {
  const links = ['Vision', 'HCLS Impact', 'Features', 'Use Case', 'Technical Framework'];
  return (
    <nav className="sticky top-0 z-50 flex h-16 items-center justify-between border-b border-[#2a2a2a] bg-[#0d0d0d] px-12">
      <LikaLogo />
      <div className="hidden items-center gap-9 lg:flex">
        {links.map(link => (
          <a
            key={link}
            href={`#${link.toLowerCase().replace(/ /g, '-')}`}
            className={`text-sm font-medium ${link === 'Vision' ? 'text-[#22c55e]' : 'text-white/85'}`}
          >
            {link}
          </a>
        ))}
      </div>
      <div className="flex items-center gap-2.5">
        <a
          href="#"
          className="rounded-lg border border-white px-4 py-2 text-sm font-medium text-white font-['Inter']"
        >
          Book Consultation
        </a>
        <a
          href="/login"
          className="rounded-lg bg-[#16a34a] px-4 py-2 text-sm font-semibold text-white font-['Inter']"
        >
          Try Demo
        </a>
      </div>
    </nav>
  );
}

function HeroWave() {
  return (
    <div className="absolute bottom-0 left-0 w-full">
      <img src="/background.svg" alt="Background Wave" className="w-full h-auto" />
    </div>
  );
}

function Hero() {
  return (
    <section id="vision" className="relative bg-[#171717] pt-40 pb-80 text-center overflow-hidden">
      <div className="relative z-10 mx-auto max-w-[1036px] px-6 pb-20">
        <p className="mb-3 text-[clamp(30px,58px,58px)] font-normal leading-[1.15] text-white">
          Natural conversations. Real results.
        </p>
        <h2 className="mb-7 text-[clamp(32px,58px,58px)] font-extrabold text-[#00A63E]">
          Turn calls into actions.
        </h2>
        <p className="mx-auto mb-10 text-align-center text-[24px] text-[#D9D9D9]">
          A ready-to-integrate voice AI platform that transforms how businesses communicate,
          ensuring every interaction is captured, understood, and actionable.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-3.5">
          <a
            href="#"
            className="inline-flex items-center gap-2 rounded-lg bg-[#16a34a] px-7 py-[13px] text-[15px] font-semibold text-white font-['Inter']"
          >
            Try Interactive Demo <ArrowRight size={16} />
          </a>
          <a
            href="#"
            className="inline-flex items-center gap-2 rounded-lg bg-[#2a2a2a] px-7 py-[13px] text-[15px] font-semibold text-white font-['Inter']"
          >
            Talk to our Expert
          </a>
        </div>
      </div>
      <HeroWave />
    </section>
  );
}

function ProductVision() {
  return (
    <section id="hcls-impact" className="bg-[#dcf5e4] px-12 py-20">
      <div className="mx-auto grid max-w-[1180px] gap-16 lg:grid-cols-2 lg:items-center">
        <div>
          <span className="mb-6 inline-block rounded-full border border-[#ddd] bg-white px-3.5 py-1 text-[11px] font-bold tracking-[0.06em] text-[#00592D] font-['Inter']">
            PRODUCT VISION
          </span>
          <p className="mb-5 text-[clamp(28px,36px,42px)] leading-[1.2] text-[#00381C]">
            Human-standard AI voice agent,{' '}
            <span className="font-extrabold">a new era of voice interaction</span>
          </p>
          <p className="mb-8 leading-[1.7] text-[#3E4A3E] text-sm font-thin">
            To be a seamless, plug-and-play voice AI platform that revolutionizes business
            communication by making every single conversation valuable and effective.
          </p>
          <div className="space-y-6">
            <div className="flex gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#e2e8f0] bg-white text-[#444]">
                <img src="/latancy.svg" alt="Transcript Icon" />
              </div>
              <div>
                <p className="mb-1 text-[15px] font-semibold text-[#1B1C1C]">Lowest Latency</p>
                <p className="text-sm leading-[1.6] text-[#555] text-[14px]">
                  Built specifically for healthcare, handling interrupted speech and complex
                  multi-step requests.
                </p>
              </div>
            </div>
            <div className="flex gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-[#e2e8f0] bg-white text-[#444]">
                <img src="/phone.svg" alt="Transcript Icon" />
              </div>
              <div>
                <p className="mb-1 text-[15px] font-semibold text-[#1B1C1C]">
                  100% audit coverage.
                </p>
                <p className="text-sm leading-[1.6] text-[#555] font-['Inter']">
                  Every call is recorded, transcribed, and structured instantly legal-team ready.
                </p>
              </div>
            </div>
          </div>
        </div>
        <div className="p-6">
          <img src="/product_vision.svg" alt="Hero Agent" className="w-full" />
        </div>
      </div>
    </section>
  );
}

function Features() {
  const cards = [
    {
      icon: <img src="/call.svg" alt="Inboud & Outbound call" />,
      title: 'Inbound & Outbound Calls',
      desc: 'Handle instant support calls or scale proactive outreach with 90% resolution rates.',
    },
    {
      icon: <img src="/knowledge_base_integration.svg" alt="Knowledge base icon" />,
      title: 'Knowledge Base Integration',
      desc: 'Connect PDFs, CSV files to give your agent authoritative, script-free answers.',
    },
    {
      icon: <img src="/structured-data.svg" alt="structured data extraction icon" />,
      title: 'Structured Data Extraction',
      desc: 'Instantly convert raw voice interactions into actionable data and automatic next steps.',
    },
    {
      icon: <img src="/conversation.svg" alt="natural conversation icon" />,
      title: 'Natural Conversation',
      desc: 'True human-parity dialogue that pauses and adapts immediately when the user speaks.',
    },
    {
      icon: <img src="/Hipaa-ready.svg" alt="natural conversation icon" />,
      title: 'HIPAA Ready',
      desc: 'VPC-native, HIPAA-eligible services, and PHI awareness built-in.',
    },
    {
      icon: <img src="/Healthcare-ready.svg" alt="health care ready" />,
      title: 'Healthcare Ready',
      desc: 'Ready for mission-critical deployments with SOC2, HIPAA, and GDPR compliance built into the foundation.',
      green: true,
    },
  ];

  return (
    <section id="features" className="bg-[#111] px-12 py-20">
      <div className="mx-auto max-w-[1180px]">
        <h2 className="mb-14 text-center text-4xl font-bold text-white">
          Enterprise-Grade Features
        </h2>
        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
          {cards.map(card => (
            <div
              key={card.title}
              className={`rounded-[14px] border px-8 py-9 ${card.green ? 'border-transparent bg-[#16a34a]' : 'border-[#2a2a2a] bg-[#1a1a1a]'}`}
            >
              <div className={`mb-5 ${card.green ? 'text-white/80' : 'text-[#22c55e]'}`}>
                {card.icon}
              </div>
              <h3 className="mb-2.5 text-[17px] font-bold text-white">{card.title}</h3>
              <p
                className={`text-sm leading-[1.65] font-['Inter'] ${card.green ? 'text-white/80' : 'text-[#a1a1aa]'}`}
              >
                {card.desc}
              </p>
              {card.green && (
                <a
                  href="#"
                  className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-white px-4 py-2 text-[13px] font-semibold text-[#16a34a] font-['Inter']"
                >
                  Explore Full Technical Docs <ExternalLink size={12} />
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function UseCases() {
  const items = [
    {
      title: 'Appointment Scheduling',
      description:
        'Automated appointment scheduling that saves clinical staff hours of manual calling',
      image: '/usecase-appointment-scheduling.svg',
    },
    {
      title: 'Reminder & Status Check',
      description:
        'Automated patient outreach for appointment reminders and post- care follow-ins.',
      image: '/Usecase-reminder-status.svg',
    },
    {
      title: 'Prescription Refills',
      description: 'LIKA ensured I never missed a refill and scheduled my follow- up in seconds.',
      image: '/Usecase-Prescription-refills.svg',
    },
  ];
  return (
    <section id="use-case" className="bg-[#dcf5e4] px-12 py-20">
      <div className="mx-auto max-w-[1180px]">
        <h2 className="mb-14 text-center text-4xl text-[#1B1C1C]">
          Real-World Impact: <strong className="font-extrabold">Proven Success</strong>
        </h2>
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {Object(items).map(({ title, description, image }) => (
            <div key={title} className="rounded-[14px] border border-[#e5e7eb] bg-white p-8">
              <h3 className="mb-3 text-lg font-bold text-[#111]">{title}</h3>
              <p className="text-sm leading-[1.65] text-[#555] font-thin">{description}</p>
              <div className="rounded-xl  p-6 text-white">
                <img src={image} alt="Hero Agent" className="w-full rounded-lg" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const AFramework = () => {
  return (
    <section className="bg-black text-white py-6 px-6" id="technical-framework">
      <div className="my-9 py-9">
        <div className="max-w-6xl mx-auto py-4">
          <div className="text-center mb-12">
            <span className="bg-[#EF9B26] text-black py-1 px-3 rounded-full text-xs font-semibold">
              AWS UTILIZATION
            </span>
            <h1 className="text-3xl md:text-4xl mt-4">
              AWS-Backed <span className="font-extrabold">Technical Framework</span>
            </h1>
            <p className="md:text-lg text-gray-400 mt-4 text-white font-light">
              LIKA utilizes a high-level AWS framework to ensure maximum uptime and data
              sovereignty.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-left border-t border-l border-r border-gray-600">
            <div className="p-4 border-r border-gray-600 mt-5 mb-5">
              <h3 className="text-lg font-bold text-[#EF9B26] mb-2">Amazon Bedrock</h3>
              <p className="text-white">
                Secure access to top-tier speech-to-speech model (Nova Sonic) for realtime
                transcription and response generation.
              </p>
            </div>
            <div className="p-4 border-r border-gray-600 mt-5 mb-5">
              <h3 className="text-lg font-bold text-[#EF9B26] mb-2">AWS Fargate</h3>
              <p className="text-white">
                A serverless, queue-based architecture that scales horizontally based on your
                document volume.
              </p>
            </div>
            <div className="p-4 border-r border-gray-600 mt-5 mb-5">
              <h3 className="text-lg font-bold text-[#EF9B26] mb-2">S3 & CloudWatch</h3>
              <p className="text-white">
                Full observability support and secure storage within your own AWS boundary.
              </p>
            </div>
          </div>
          <div className="bg-[#393939] flex justify-center items-center mt-8">
            <img
              src="Aws-Partner.png"
              alt="AWS Advanced Tier Services"
              className="h-auto w-full max-w-lg sm:max-w-xl md:max-w-2xl lg:max-w-3xl xl:max-w-4xl 2xl:max-w-5xl"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

function CTA() {
  return (
    <section className="bg-[#007E40] px-12 py-24 text-center">
      <div className="mx-auto max-w-[640px]">
        <h2 className="mb-4 text-4xl text-white">Ready to end the era of "Press 1"?</h2>
        <p className="mb-11 text-sm leading-[1.6] text-white/85 font-['Inter']">
          Deploy LIKA to your AWS environment today and start having real conversations with your
          customers.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          <a
            href="#"
            className="rounded-lg bg-[#1a2030] px-7 py-3 text-sm font-semibold text-[#ff9900] font-['Inter']"
          >
            <span className="d-block text-white">DEPLOYED ON</span>
            <img src="/aws-marketplace.png" alt="AWS Logo" className="h-4 w-auto" />
          </a>
          <a
            href="#"
            className="rounded-lg bg-[#111] px-8 py-4.5 text-[15px] font-bold text-white font-['Inter']"
          >
            Talk to Our Experts
          </a>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#2a2a2a] bg-[#0a0a0a] px-12 py-10">
      <div className="mx-auto max-w-[1180px]">
        <div className="mb-5 flex flex-wrap items-center justify-between gap-4">
          <LikaLogo />
          <div className="flex flex-wrap gap-8">
            {['Privacy Policy', 'Terms of Service', 'AWS Marketplace'].map(link => (
              <a
                key={link}
                href="#"
                className="text-xs font-medium uppercase tracking-[0.04em] text-[#888] font-['Inter']"
              >
                {link}
              </a>
            ))}
          </div>
        </div>
        <p className="text-xs tracking-[0.04em] text-[#555] font-['Inter']">
          © 2026 LEAPFROG TECHNOLOGY
        </p>
      </div>
    </footer>
  );
}

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#0d0d0d] font-['Inter'] scroll-smooth">
      <TopBanner />
      <Navbar />
      <Hero />
      <ProductVision />
      <Features />
      <UseCases />
      <AFramework />
      <CTA />
      <Footer />
    </div>
  );
}
